import {
  FileSource,
  AccountingReportPartTypeEnum,
  AccountingFileType,
  MeritInvoiceShipmentResult,
} from '@/generated/graphql';

// Supported sources for Merit actions
const SUPPORTED_INVOICE_SOURCES = new Set<FileSource>([
  FileSource.Invoicing,
  FileSource.Invoice,
  FileSource.Chargebee,
]);

/**
 * Checks if a file's source is eligible for Merit (Enty Invoicing/Invoice or Chargebee)
 */
export const isMeritEligibleSource = (source?: FileSource | null): boolean =>
  !!source && SUPPORTED_INVOICE_SOURCES.has(source);

/**
 * Checks if a subfolder type is eligible to show Merit-related actions
 * We allow both Outgoing and Incoming subfolders now.
 */
export const isMeritEligibleSubfolderType = (
  type?: AccountingReportPartTypeEnum | null,
): boolean => {
  return (
    type === AccountingReportPartTypeEnum.Outgoing ||
    type === AccountingReportPartTypeEnum.Incoming
  );
};

/**
 * Checks if the file name mentions "credit note" (case-insensitive) anywhere,
 * with spaces, hyphens or underscores between the words.
 * Examples that return true:
 *  - "Credit note 123.pdf"
 *  - "INV-42 credit-note.pdf"
 *  - "credit_note.pdf"
 */
export const containsCreditNoteInName = (name?: string | null): boolean => {
  if (!name) return false;
  return /credit[\s_-]*note/i.test(name);
};

const MONITE_INVOICE_DOCUMENT_TYPE = 'INVOICE';
const MONITE_CREDIT_NOTE_DOCUMENT_TYPE = 'CREDIT NOTE';

/**
 * Normalizes OCR documentType (casing, CREDIT_NOTE spelling) for comparison.
 */
const normalizeMoniteDocumentType = (documentType?: string | null): string =>
  (documentType ?? '').trim().replace(/_/g, ' ').toUpperCase();

/**
 * Checks if a file is a regular accounting invoice or credit note recognized by Monite
 */
export const isMoniteInvoice = (
  row?: Pick<AccountingFileType, 'moniteInvoice'> | null,
): boolean => {
  const documentType = normalizeMoniteDocumentType(row?.moniteInvoice?.documentType);
  return (
    documentType === MONITE_INVOICE_DOCUMENT_TYPE ||
    documentType === MONITE_CREDIT_NOTE_DOCUMENT_TYPE
  );
};

const PURCHASE_INVOICE_SOURCES = new Set<FileSource>([
  FileSource.Manual,
  FileSource.Mail,
  FileSource.Accountant,
]);

/**
 * Uploaded/mailed files whose type is recognized by Monite OCR.
 * The sendPurchaseInvoiceToMerit mutation then classifies them as purchase or sales.
 */
export const isOcrInvoiceSource = (source?: FileSource | null): boolean =>
  !!source && PURCHASE_INVOICE_SOURCES.has(source);

/**
 * Checks if a file should be sent via sendPurchaseInvoiceToMerit:
 * an uploaded/mailed file that Monite recognized as an invoice or credit note.
 * Backend routes it to purchase or sales.
 */
export const isPurchaseInvoiceRow = (
  row?: Pick<AccountingFileType, 'source' | 'moniteInvoice'> | null,
): boolean => isOcrInvoiceSource(row?.source) && isMoniteInvoice(row);

/**
 * Composed check for whether the Merit action should be hidden for a given row.
 * Internally calls existing helpers to keep logic in one place.
 */
export const isMeritActionHiddenForRow = (
  row: Pick<AccountingFileType, 'source' | 'name' | 'moniteInvoice'> | null | undefined,
  subfolderType?: AccountingReportPartTypeEnum | null,
): boolean => {
  // Merit only makes sense for the invoice subfolders
  if (!isMeritEligibleSubfolderType(subfolderType)) {
    return true;
  }

  // Credit notes are recognized by file name regardless of source, because OCR
  // often fails to classify them and the name is the only signal we have
  if (containsCreditNoteInName(row?.name)) {
    return false;
  }

  // Uploaded/mailed Monite invoices and credit notes: Incoming (purchase) and Outgoing (sales)
  if (isPurchaseInvoiceRow(row)) {
    return false;
  }

  return !isMeritEligibleSource(row?.source);
};

/**
 * Checks if a row must be sent with sendPurchaseInvoiceToMerit instead of the
 * Enty invoicing mutation: OCR-recognized uploads, plus credit notes recognized
 * by file name only. Everything that is not an Enty invoicing or Chargebee
 * document is read from the accounting file, and the backend classifies it as
 * purchase or sales itself.
 */
export const usesPurchaseInvoiceMutation = (
  row?: Pick<AccountingFileType, 'source' | 'name' | 'moniteInvoice'> | null,
): boolean =>
  isPurchaseInvoiceRow(row) ||
  (containsCreditNoteInName(row?.name) && !isMeritEligibleSource(row?.source));

/**
 * How far a document got on its way to Merit.
 *
 * The state comes from merit_invoice_shipment on the backend and reaches the
 * front-end flattened onto the accounting file as meritShipmentStatus /
 * meritShipmentError, so every query that returns a file can carry it.
 */
export enum MeritShipmentStatus {
  // Merit is not involved in this file at all (wrong subfolder, not an invoice)
  NotApplicable = 'notApplicable',
  Sent = 'sent',
  Error = 'error',
  NotSent = 'notSent',
}

export type MeritStatusRow = Pick<
  AccountingFileType,
  'source' | 'name' | 'moniteInvoice' | 'meritInvoiceId' | 'meritRefNo'
> & {
  meritShipmentStatus?: MeritInvoiceShipmentResult | null;
  meritShipmentError?: string | null;
};

/**
 * Resolves the Merit state of a file. A file with a Merit invoice id is sent no
 * matter what the shipment says; without one, the shipment is the only thing
 * that distinguishes a failed attempt from a document nobody sent yet.
 */
export const getMeritShipmentStatus = (
  row: MeritStatusRow | null | undefined,
  subfolderType?: AccountingReportPartTypeEnum | null,
): MeritShipmentStatus => {
  if (isMeritActionHiddenForRow(row, subfolderType)) {
    return MeritShipmentStatus.NotApplicable;
  }

  if (row?.meritInvoiceId || row?.meritShipmentStatus === MeritInvoiceShipmentResult.Ok) {
    return MeritShipmentStatus.Sent;
  }

  if (row?.meritShipmentStatus === MeritInvoiceShipmentResult.Error) {
    return MeritShipmentStatus.Error;
  }

  return MeritShipmentStatus.NotSent;
};

/**
 * The document reached Merit — by its invoice id or by a successful shipment.
 */
export const isSentToMerit = (
  row: MeritStatusRow | null | undefined,
  subfolderType?: AccountingReportPartTypeEnum | null,
): boolean => getMeritShipmentStatus(row, subfolderType) === MeritShipmentStatus.Sent;

/**
 * Whether the send action should be offered for a file at all.
 *
 * Hidden both when Merit has nothing to do with the file and when it is already
 * there: the Merit status is shown next to the file, so a permanently disabled
 * send button would only repeat it. A failed shipment keeps the action — that is
 * the retry.
 */
export const isMeritSendActionHidden = (
  row: MeritStatusRow | null | undefined,
  subfolderType?: AccountingReportPartTypeEnum | null,
): boolean => {
  const status = getMeritShipmentStatus(row, subfolderType);

  return status === MeritShipmentStatus.NotApplicable || status === MeritShipmentStatus.Sent;
};

export interface IMeritStatusView {
  status: MeritShipmentStatus;
  label: string;
  color: string;
  textColor: string;
  tooltip: string;
  errorMessage?: string;
}

/**
 * How the Merit state is presented, shared by the file table and the document
 * viewers so the same document never reads differently in two places.
 */
export const getMeritStatusView = (
  row: MeritStatusRow | null | undefined,
  subfolderType?: AccountingReportPartTypeEnum | null,
): IMeritStatusView => {
  const status = getMeritShipmentStatus(row, subfolderType);

  switch (status) {
    case MeritShipmentStatus.Sent:
      return {
        status,
        label: 'Sent',
        color: 'green-1',
        textColor: 'green-9',
        tooltip: row?.meritRefNo ? `In Merit as ${row.meritRefNo}` : 'In Merit',
      };
    case MeritShipmentStatus.Error:
      return {
        status,
        label: 'Send failed',
        color: 'red-1',
        textColor: 'red-9',
        tooltip: 'The last attempt to send this invoice to Merit failed',
        errorMessage: row?.meritShipmentError || 'Merit did not report a reason',
      };
    default:
      return {
        status,
        label: 'Not sent',
        color: 'grey-3',
        textColor: 'grey-8',
        tooltip: 'This invoice has not been sent to Merit yet',
      };
  }
};
